import { FunctionComponent, JSX } from 'preact';
import { useCallback, useEffect, useMemo, useRef } from 'preact/hooks';
import cn from 'classnames';

import { CloseButton } from './CloseButton';
import { OverlayContext } from './OverlayContext';

export interface OverlayProps {
  onCloseClick: () => void;
  disableCloseButton?: boolean;
  className?: string;
  contentOverlayClassName?: string;
}

export const Overlay: FunctionComponent<OverlayProps> = ({
  children,
  onCloseClick,
  disableCloseButton,
  className,
  contentOverlayClassName,
}) => {
  const focusTrapRef = useRef<HTMLDivElement>(null!);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleKeydown: JSX.KeyboardEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (e.code === 'Escape') {
        onCloseClick();
      }
    },
    [onCloseClick]
  );

  const handleOutsideClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!contentRef.current?.contains(event.target as Node)) {
      onCloseClick();
    }
  };

  useEffect(() => {
    focusTrapRef.current.focus();
  }, []);

  const state = useMemo(() => {
    return {
      onCloseClick,
    };
  }, [onCloseClick]);

  return (
    <OverlayContext.Provider value={state}>
      <div
        className={cn('vgp-overlay', className)}
        role="presentation"
        onKeyDown={handleKeydown}
        onMouseDown={handleOutsideClick}
        tabIndex={0}
        ref={focusTrapRef}
      >
        {!disableCloseButton && (
          <CloseButton className="vgp-overlay-close-button" />
        )}
        <div
          ref={contentRef}
          className={cn('vgp-overlay-content', contentOverlayClassName)}
        >
          {children}
        </div>
      </div>
    </OverlayContext.Provider>
  );
};
